import {
  buildRequestOptionsJsonBody,
} from '@quintoandar/fwp-fetch';
import { ChecklistMapper } from 'models/mapping/ChecklistMapper';
import {
  startedPhotoSessionsURL,
  notStartedPhotoSessionsURL,
  photoSessionDetailURL,
  startPhotoSessionURL,
  issueReportURL,
  getChecklistURL,
  finishChecklistSessionURL,
  reportReasonsURL,
  getScheduleDatesURL,
  rescheduleFixedSessionURL,
  rescheduleFlexibleSessionURL,
  availablePhotoJobTypes,
  getStepsProgressURL,
  publishPhotoSessionURL,
} from './urls';
import { v2Header } from './utils';
import { request, postRequest } from '../utils/request-utils';

export const fetchNotStartedPhotoSessions = ({ includeProblem } = {}) =>
  request(notStartedPhotoSessionsURL({ includeProblem }));

export const fetchStartedPhotoSessions = () =>
  request(startedPhotoSessionsURL());

export const fetchPhotoSessionDetail = photoSessionId =>
  request(photoSessionDetailURL(photoSessionId));

export const startPhotoSession = photoSessionId =>
  request(
    startPhotoSessionURL(photoSessionId),
    buildRequestOptionsJsonBody('PUT'),
  );

/** @returns {Promise<ReportIssue[]>} */
export const fetchReportIssues = async () =>
  request(reportReasonsURL(), { method: 'GET', headers: v2Header() });

export const reportIssue = async ({
  photoSessionId,
  issue,
  note: explanation,
}) =>
  request(
    issueReportURL(photoSessionId),
    buildRequestOptionsJsonBody('PUT', { issue, explanation }),
  );

export const finishChecklistSession = async ({
  photoSessionId,
  checklist,
  cachedSessionProperty,
}) =>
  request(
    finishChecklistSessionURL(photoSessionId),
    buildRequestOptionsJsonBody(
      'POST',
      await ChecklistMapper.fromStore({
        checklist,
        photoSessionId,
        cachedSessionProperty,
      }),
    ),
  );

export const fetchChecklist = () => request(getChecklistURL());

/**
 * @param {number} propertyId - id of the property that will have session rescheduled
 * @param {?number} agentUserId - user id of the logged photographer; `null`/`undefined` returns the
 *   region-wide availability instead of narrowing it down to this photographer
 * @returns {Promise<ScheduleDatesResponse>} */
export const fetchSchedulesDates = (propertyId, agentUserId) =>
  request(getScheduleDatesURL(propertyId, agentUserId));

/**
 * @param {number} propertyId - id of the property that will have session rescheduled
 * @param {RescheduleFixedPayload} body
 * @returns {Promise<RescheduleResponse>} */
export const rescheduleFixedSession = ({ propertyId, body }) =>
  postRequest(rescheduleFixedSessionURL(propertyId), body);

/**
 * @param {number} propertyId
 * @param {RescheduleFlexiblePayload} body
 * @returns {Promise<RescheduleResponse>} */
export const rescheduleFlexibleSession = ({ propertyId, body }) =>
  postRequest(rescheduleFlexibleSessionURL(propertyId), body);

/**
 * @param {number} propertyId
 * @returns {Promise<AvailablePhotoJobTypesResponse>} */
export const fetchAvailablePhotoJobTypes = propertyId =>
  request(availablePhotoJobTypes(propertyId));

export const fetchStepsProgress = async ({ photoSessionId, propertyId }) =>
  request(
    getStepsProgressURL(),
    buildRequestOptionsJsonBody('POST', { photoSessionId, propertyId }),
  );

export const publishPhotoSession = async ({
  checklist,
  photoSessionId,
  houseId,
  cachedSessionProperty,
}) =>
  request(
    publishPhotoSessionURL(),
    buildRequestOptionsJsonBody('PUT', {
      checklist: await ChecklistMapper.fromStore({
        checklist,
        photoSessionId,
        cachedSessionProperty,
      }),
      photoSessionId,
      houseId,
    }),
  );
